// cspell:ignore Topup
import { inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PrivateMessageEventConsumerService } from '../events/private-message/private-message-event.consumer.service';
import { groupMessageAmounts } from '../core/private-messages/private-message-text.utils';
import { ToastService } from './toast.service';
import { ORDER_ERROR_CODES } from '../constants/order-error-code';

/**
 * One order-state value the legacy toast reacted to, mapped to the toast it raised.
 * States not listed here (e.g. `OnSending`, `Error`, anything unseen) show nothing —
 * the legacy `showOrderStateToast` stayed silent on them too, and `Error` already
 * reaches the user through the text-message path.
 */
const ORDER_STATE_TOASTS: Readonly<Record<string, { message: string; success: boolean }>> = {
	OrderExecuted: { message: 'سفارش با موفقیت انجام شد', success: true },
	Done: { message: 'سفارش با موفقیت انجام شد', success: true },
	PartiallyExcution: { message: 'بخشی از سفارش انجام شد', success: true },
	OnBoard: { message: 'سفارش در سیستم معاملات ثبت شد', success: true },
	Cancel: { message: 'سفارش از سیستم معاملات حذف شد', success: true },
	OnModifyBoard: { message: 'سفارش با موفقیت ویرایش شد', success: true }
};

/** Wallet-transfer failure types the vendor sends as `Text_IME`, mapped to the error toast they raise. */
const MALFORMED_TRANSFER_TOASTS: Readonly<Record<string, string>> = {
	InstantPaymentTopupRequestTransitionedToMalformed: 'واریز پول به کیف اصلی با خطا مواجه شد.',
	InstantPaymentTransferRequestTransitionedToMalformed: 'واریز پول به کیف مقصد با خطا مواجه شد.',
	TransferRequestTransitionedToMalformed: 'انتقال وجه به کیف مقصد با خطا مواجه شد.'
};

/**
 * Shows the text the server addresses to this user.
 *
 * A consumer of the private-message bus like any other — it knows the text and
 * nothing about where it came from. It exists to demonstrate the point of the
 * split as much as to raise the toast: a second consumer is added the same way,
 * without `PrivateMessageService` learning that it has one.
 *
 * The vendor's text is shown as a success. It reports something that has just
 * happened in the account — a transfer landing, for one — and carries its amounts
 * as plain digit runs, which are grouped for reading. `orderstate` is the
 * machine-readable signal for an order's state, and this service raises the toast
 * for it too: the message per state is carried over from the legacy
 * `showOrderStateToast`, so only the variants the wire marks as a success are green.
 *
 * Instantiated at startup by `provideEventDrivenStartup`.
 */
@Service()
export class PrivateMessageNotificationService {
	private readonly privateMessages = inject(PrivateMessageEventConsumerService);
	private readonly toastService = inject(ToastService);

	constructor() {
		this.privateMessages.textReceived$.pipe(takeUntilDestroyed()).subscribe(message => {
			const { text, type, kind } = message;
			if (kind === 'Text_IME' && type === '1') {
				// The text is the vendor reporting what just happened — a transfer landing, for
				// one — so it is shown as the success it is. Its amounts arrive as plain digit
				// runs and are grouped here; the vendor does not format them.
				this.toastService.success(groupMessageAmounts(text));
			}

			const malformedMessage = MALFORMED_TRANSFER_TOASTS[type];
			if (kind === 'Text_IME' && malformedMessage) {
				this.toastService.error(malformedMessage);
			}
		});

		this.privateMessages.orderStateChanged$.pipe(takeUntilDestroyed()).subscribe(state => {
			if (state.orderState === 'Error') {
				this.toastService.error(ORDER_ERROR_CODES[state.errorCode] ?? 'خطا');
				return;
			}

			const toast = ORDER_STATE_TOASTS[state.orderState];
			if (!toast) {
				return;
			}

			const message = toast.message;
			if (toast.success) {
				this.toastService.success(message);
			} else {
				this.toastService.info(message);
			}
		});
	}
}
